import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Edit, 
  Trash2, 
  Shield, 
  Baby,
  User,
  Building2,
  Clock
} from 'lucide-react';
import { FamilyContact } from '@/hooks/useContacts';

interface ContactCardProps {
  contact: FamilyContact;
  onEdit?: () => void;
  onDelete?: () => void;
  onCall?: (phone: string) => void;
  onEmail?: (email: string) => void;
}

const contactTypeLabels = {
  doctor: 'Médecin',
  pediatrician: 'Pédiatre',
  dentist: 'Dentiste',
  school: 'École',
  teacher: 'Enseignant(e)',
  babysitter: 'Baby-sitter',
  nanny: 'Nounou',
  daycare: 'Crèche',
  family_friend: 'Ami de la famille',
  neighbor: 'Voisin(e)',
  emergency: 'Urgence',
  pharmacy: 'Pharmacie',
  sports_club: 'Club de sport',
  other: 'Autre'
};

const contactTypeColors = {
  doctor: 'bg-red-100 text-red-800 border-red-200',
  pediatrician: 'bg-pink-100 text-pink-800 border-pink-200',
  dentist: 'bg-blue-100 text-blue-800 border-blue-200',
  school: 'bg-green-100 text-green-800 border-green-200',
  teacher: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  babysitter: 'bg-purple-100 text-purple-800 border-purple-200',
  nanny: 'bg-violet-100 text-violet-800 border-violet-200',
  daycare: 'bg-orange-100 text-orange-800 border-orange-200',
  family_friend: 'bg-cyan-100 text-cyan-800 border-cyan-200',
  neighbor: 'bg-teal-100 text-teal-800 border-teal-200',
  emergency: 'bg-red-100 text-red-800 border-red-200',
  pharmacy: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  sports_club: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  other: 'bg-gray-100 text-gray-800 border-gray-200'
};

export const ContactCard = ({ contact, onEdit, onDelete, onCall, onEmail }: ContactCardProps) => {
  const initials = `${contact.first_name[0]}${contact.last_name[0]}`.toUpperCase();

  return (
    <Card className="shadow-soft hover:shadow-medium transition-shadow">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <Avatar>
              <AvatarFallback className="bg-primary/10 text-primary">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <CardTitle className="text-base">
                {contact.first_name} {contact.last_name}
              </CardTitle>
              {contact.organization && (
                <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                  <Building2 className="h-3 w-3" />
                  {contact.organization}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {contact.is_emergency_contact && (
              <Badge variant="destructive" className="text-xs">
                <Shield className="h-3 w-3 mr-1" />
                Urgence
              </Badge>
            )}
            <Badge 
              variant="secondary" 
              className={`text-xs border ${contactTypeColors[contact.contact_type]}`}
            >
              {contactTypeLabels[contact.contact_type]}
            </Badge>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Child association */}
        {contact.child_member_name && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Baby className="h-4 w-4" />
            <span>Contact pour {contact.child_member_name}</span>
          </div>
        )}

        {/* Contact information */}
        <div className="space-y-3">
          {contact.phone_primary && (
            <div className="flex items-center gap-3">
              <Phone className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm flex-1">{contact.phone_primary}</span>
              <Button 
                size="sm" 
                variant="outline" 
                onClick={() => onCall?.(contact.phone_primary!)}
              >
                <Phone className="h-3 w-3" />
              </Button>
            </div>
          )}

          {contact.phone_secondary && (
            <div className="flex items-center gap-3">
              <Phone className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm flex-1">{contact.phone_secondary} (2e)</span>
              <Button 
                size="sm" 
                variant="outline" 
                onClick={() => onCall?.(contact.phone_secondary!)}
              >
                <Phone className="h-3 w-3" />
              </Button>
            </div>
          )}

          {contact.email && (
            <div className="flex items-center gap-3">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm flex-1 truncate">{contact.email}</span>
              <Button 
                size="sm" 
                variant="outline" 
                onClick={() => onEmail?.(contact.email!)}
              >
                <Mail className="h-3 w-3" />
              </Button>
            </div>
          )}

          {contact.address && (
            <div className="flex items-start gap-3">
              <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
              <span className="text-sm flex-1">{contact.address}</span>
            </div>
          )}

          {contact.availability_notes && (
            <div className="flex items-start gap-3">
              <Clock className="h-4 w-4 text-muted-foreground mt-0.5" />
              <span className="text-sm flex-1">{contact.availability_notes}</span>
            </div>
          )}

          {contact.notes && (
            <div className="p-3 bg-muted/30 rounded-lg">
              <p className="text-sm">{contact.notes}</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-2 border-t">
          <span className="text-xs text-muted-foreground">
            Ajouté par {contact.created_by_name}
          </span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={onEdit}>
              <Edit className="h-3 w-3 mr-1" />
              Modifier
            </Button>
            <Button size="sm" variant="outline" onClick={onDelete}>
              <Trash2 className="h-3 w-3 mr-1" />
              Supprimer
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};